import { useEffect } from "react";
import { useLocation, useNavigate, useNavigation } from "react-router";
import { useShowFilters } from "src/components/features/medium/AssetsFilter/AssetsFilters.hooks";
import { resolveTimeWindowUrlParam } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.selection";
import { useSearchStorage } from "src/storage/useSearchStorage";
import { URL_ARRAY_SEPARATOR } from "src/utils/constants";

// This hook is used to sync the search params with the storage params,
// so the search params are always present if they are defined in the storage
export const useSyncSearchParamsWithStorage = () => {
  const { state } = useNavigation();
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const storageParams = useSearchStorage();
  const { showTagsFilter, showUsersFilter } = useShowFilters();

  // Check and add missing parameters when navigation is idle (finished)
  useEffect(() => {
    // Only check when navigation is idle and we have a current location
    if (state === "idle" && currentLocation) {
      const searchParams = new URLSearchParams(currentLocation.search);
      let searchModified = false;

      // Always keep a valid selection in the URL. Missing → stored token (or
      // last-7-days). Present but invalid (legacy minutes) → last-7-days, never
      // storage — see resolveTimeWindowUrlParam.
      const rawTimeWindow = searchParams.get("timeWindow");
      const nextTimeWindow = resolveTimeWindowUrlParam(
        rawTimeWindow,
        storageParams.timeWindow
      );
      if (rawTimeWindow !== nextTimeWindow) {
        searchParams.set("timeWindow", nextTimeWindow);
        searchModified = true;
      }

      // Only add optional search params if filters are shown, params missing, AND storage has values
      // This prevents overriding existing URL parameters and avoids polluting filter-hidden routes
      if (
        showTagsFilter &&
        !searchParams.has("appTags") &&
        storageParams.appTags?.length
      ) {
        searchParams.set("appTags", storageParams.appTags.join(URL_ARRAY_SEPARATOR));
        searchModified = true;
      }

      if (
        showUsersFilter &&
        !searchParams.has("appOwners") &&
        storageParams.appOwners?.length
      ) {
        searchParams.set(
          "appOwners",
          storageParams.appOwners.join(URL_ARRAY_SEPARATOR)
        );
        searchModified = true;
      }

      if (searchModified) {
        // Replace the current URL with the updated search params
        const newUrl = `${currentLocation.pathname}?${searchParams.toString()}${currentLocation.hash}`;

        void navigate(newUrl, {
          replace: true, // Use replace to avoid creating a new history entry
          preventScrollReset: true, // Prevent scroll reset during replacement
        });
      }
    }
  }, [
    state,
    currentLocation,
    navigate,
    storageParams,
    showTagsFilter,
    showUsersFilter,
  ]);
};
